/**
 * Générateur — Résoudre des problèmes de proportionnalité par des procédures élémentaires
 * Attendu ID : 684 | Niveau : 6ème | Domaine : Proportionnalité (cluster 42)
 *
 * Types générés (1 catégorie, 6 formats) :
 *   - retour_unite_saisie     : saisie — calcul via retour à l'unité
 *   - linearite_mult_saisie   : saisie — linéarité multiplicative (multiple simple)
 *   - linearite_add_saisie    : saisie — linéarité additive
 *   - coefficient_saisie      : saisie — calcul via coefficient
 *   - procedure_qcm           : QCM 4 choix — identifier la procédure adaptée
 *   - recette_saisie          : saisie — problème contextuel recette
 *
 * Spécificité : 100 % texte. Valeurs numériques choisies pour que les quotients
 * et produits tombent justes (1-2 décimales maximum).
 *
 * Partition avec attendus voisins :
 *   - 682 : définition / expressions.
 *   - 683 : identifier si la situation relève du modèle.
 *   - 685 : tableau de proportionnalité / notations symboliques (flèches).
 *
 * Cible : 20/20 uniques sur 20 itérations, 5 runs consécutifs minimum.
 */

import {
    randomChoice,
    makeQcm, makeSaisie,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 684;

// +2 fruits (revue 2026-08-10) : pool du retour à l'unité 108 → 192.
const FRUITS = ['pommes', 'poires', 'oranges', 'bananes', 'kiwis', 'cerises', 'fraises', 'raisins'];

const L_RET   = 'Retour à l\'unité (saisie)';
const L_LIN_M = 'Linéarité multiplicative (saisie)';
const L_LIN_A = 'Linéarité additive (saisie)';
const L_COEF  = 'Coefficient de proportionnalité (saisie)';
const L_PROC  = 'Choisir une procédure adaptée (QCM)';
const L_REC   = 'Problème de recette (saisie)';

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || L_RET,
});


const fmt = (n) => {
    if (Number.isInteger(n)) return String(n);
    const s = Number(n).toFixed(2);
    const trimmed = s.replace(/\.?0+$/, '');
    return trimmed.replace('.', ',');
};

// ─── Format 1 — Retour à l'unité (saisie) ────────────────────

// Couples (a, p) où p/a est simple (entier ou 1 décimale), b non multiple de a.
// Tous les résultats (p/a) × b sont à 2 décimales max.
const RETOUR_CONFIGS = [
    // (a, p, b) → p/a = unit price ; résultat = unit × b
    { a: 2, p: 3,  b: 5, unit: 1.5  }, // 7,5
    { a: 2, p: 3,  b: 7, unit: 1.5  }, // 10,5
    { a: 2, p: 5,  b: 3, unit: 2.5  }, // 7,5
    { a: 2, p: 5,  b: 7, unit: 2.5  }, // 17,5
    { a: 2, p: 7,  b: 3, unit: 3.5  }, // 10,5
    { a: 2, p: 7,  b: 5, unit: 3.5  }, // 17,5
    { a: 3, p: 6,  b: 5, unit: 2    }, // 10
    { a: 3, p: 6,  b: 7, unit: 2    }, // 14
    { a: 3, p: 9,  b: 5, unit: 3    }, // 15
    { a: 3, p: 9,  b: 7, unit: 3    }, // 21
    { a: 4, p: 10, b: 5, unit: 2.5  }, // 12,5
    { a: 4, p: 10, b: 7, unit: 2.5  }, // 17,5
    { a: 4, p: 10, b: 9, unit: 2.5  }, // 22,5
    { a: 5, p: 10, b: 3, unit: 2    }, // 6
    { a: 5, p: 10, b: 7, unit: 2    }, // 14
    { a: 5, p: 15, b: 3, unit: 3    }, // 9
    { a: 5, p: 15, b: 7, unit: 3    }, // 21
    { a: 5, p: 15, b: 8, unit: 3    }, // 24
    // +6 configurations (revue 2026-08-10), résultats à 2 décimales max
    { a: 4, p: 6,  b: 5, unit: 1.5  }, // 7,5
    { a: 4, p: 6,  b: 7, unit: 1.5  }, // 10,5
    { a: 4, p: 6,  b: 9, unit: 1.5  }, // 13,5
    { a: 2, p: 9,  b: 5, unit: 4.5  }, // 22,5
    { a: 3, p: 12, b: 5, unit: 4    }, // 20
    { a: 3, p: 12, b: 7, unit: 4    }, // 28
];

// Anti-répétition immédiate (revue 2026-08-10, recette standard).
let derniereSignatureRetour = null;

const genRetourUniteSaisie = () => {
    let texte;
    let reponse;
    for (let tries = 0; tries < 12; tries++) {
        const cfg = randomChoice(RETOUR_CONFIGS);
        const fruit = randomChoice(FRUITS);
        const { a, p, b, unit } = cfg;
        reponse = unit * b;
        texte = `<p>${a} kg de ${fruit} coûtent ${fmt(p)} €. Combien coûtent ${b} kg de ${fruit} ?</p>`;
        if (texte !== derniereSignatureRetour) break;
    }
    derniereSignatureRetour = texte;

    const payload = makeSaisie(fmt(reponse), texte, {
        ...opts(L_RET),
        strict: false,
        suffixe: ' €',
    });
    // Calculatrice AUTORISÉE (décision utilisateur 2026-08-10) : divisions et
    // produits décimaux (10 ÷ 4 = 2,50 puis 2,50 × 7).
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'retour_unite_saisie';
    return payload;
};

// ─── Format 2 — Linéarité multiplicative (saisie) ────────────

// Anti-répétition immédiate (revue 2026-08-10, recette standard).
let derniereSignatureLinMult = null;

const genLineariteMultSaisie = () => {
    let payload = construireLineariteMultSaisie();
    for (let tries = 0; tries < 12 && payload.texte === derniereSignatureLinMult; tries++) {
        payload = construireLineariteMultSaisie();
    }
    derniereSignatureLinMult = payload.texte;
    return payload;
};

const construireLineariteMultSaisie = () => {
    const r = Math.random();
    const k = randomChoice([2, 3, 4, 5]);
    let texte, reponse, suffixe;

    if (r < 0.25) {
        // V1 — prix
        const fruit = randomChoice(FRUITS);
        const a = randomChoice([2, 3, 4, 5]);
        const unit = randomChoice([1.5, 2, 2.5, 3, 4]);
        const p = a * unit;
        const bigA = k * a;
        reponse = k * p;
        texte = `<p>${a} kg de ${fruit} coûtent ${fmt(p)} €. Combien coûtent ${bigA} kg de ${fruit} ?</p>`;
        suffixe = ' €';
    } else if (r < 0.5) {
        // V2 — distance/durée (trouver la durée pour k·d km)
        const t = randomChoice([1, 2, 3, 4]);
        const v = randomChoice([50, 60, 70, 80, 90]);
        const d = v * t;
        const bigD = k * d;
        reponse = k * t;
        texte = `<p>Un cycliste parcourt ${d} km en ${t} h à vitesse constante. En combien de temps parcourra-t-il ${bigD} km à la même vitesse ?</p>`;
        suffixe = ' h';
    } else if (r < 0.75) {
        // V3 — imprimante pages/min
        const t = randomChoice([2, 3, 4, 5]);
        const n = randomChoice([10, 12, 15, 20]);
        const pages = n * t;
        const bigT = k * t;
        reponse = k * pages;
        texte = `<p>Une imprimante imprime ${pages} pages en ${t} minutes. Combien de pages imprime-t-elle en ${bigT} minutes au même rythme ?</p>`;
        suffixe = ' pages';
    } else {
        // V4 — salaire horaire
        const h = randomChoice([2, 3, 4, 5]);
        const taux = randomChoice([12, 14, 15, 18, 20]);
        const s = h * taux;
        const bigH = k * h;
        reponse = k * s;
        texte = `<p>Un ouvrier gagne ${s} € pour ${h} heures de travail. Combien gagne-t-il pour ${bigH} heures au même taux horaire ?</p>`;
        suffixe = ' €';
    }

    const payload = makeSaisie(fmt(reponse), texte, {
        ...opts(L_LIN_M),
        strict: false,
        suffixe,
    });
    // Calculatrice AUTORISÉE (décision utilisateur 2026-08-10) : reconnaître
    // le multiple n'est pas immédiat sur les grands nombres (1800 = 5 × 360).
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'linearite_mult_saisie';
    return payload;
};

// ─── Format 3 — Linéarité additive (saisie) ──────────────────

// Anti-répétition immédiate (revue 2026-08-11, recette standard).
let derniereSignatureLinAdd = null;

const genLineariteAddSaisie = () => {
    let payload = construireLineariteAddSaisie();
    for (let tries = 0; tries < 12 && payload.texte === derniereSignatureLinAdd; tries++) {
        payload = construireLineariteAddSaisie();
    }
    derniereSignatureLinAdd = payload.texte;
    return payload;
};

const construireLineariteAddSaisie = () => {
    const r = Math.random();
    let texte, reponse, suffixe;

    if (r < 0.25) {
        // V1 — prix additif
        const fruit = randomChoice(FRUITS);
        const unit = randomChoice([1.5, 2, 2.5, 3]);
        const a = randomChoice([2, 3, 4]);
        const b = randomChoice([5, 6, 7]);
        const p1 = a * unit;
        const p2 = b * unit;
        reponse = p1 + p2;
        texte = `<p>Le prix est proportionnel à la masse. ${a} kg de ${fruit} coûtent ${fmt(p1)} € et ${b} kg coûtent ${fmt(p2)} €. Combien coûtent ${a + b} kg de ${fruit} ?</p>`;
        suffixe = ' €';
    } else if (r < 0.5) {
        // V2 — robinet additif
        const debit = randomChoice([3, 4, 5, 6]);
        const t1 = randomChoice([2, 3, 4]);
        const t2 = randomChoice([5, 6, 7]);
        const v1 = debit * t1;
        const v2 = debit * t2;
        reponse = v1 + v2;
        texte = `<p>Le volume d'eau est proportionnel à la durée. Le robinet remplit ${v1} L en ${t1} minutes et ${v2} L en ${t2} minutes. Combien remplit-il en ${t1 + t2} minutes au même débit ?</p>`;
        suffixe = ' L';
    } else if (r < 0.75) {
        // V3 — imprimante additive (revue 2026-08-11, enrichissement utilisateur)
        const n = randomChoice([10, 12, 15, 20]);
        const t1 = randomChoice([2, 3, 4]);
        const t2 = randomChoice([5, 6, 7]);
        const p1 = n * t1;
        const p2 = n * t2;
        reponse = p1 + p2;
        texte = `<p>Le nombre de pages est proportionnel à la durée. Une imprimante imprime ${p1} pages en ${t1} minutes et ${p2} pages en ${t2} minutes. Combien de pages imprime-t-elle en ${t1 + t2} minutes ?</p>`;
        suffixe = ' pages';
    } else {
        // V4 — distance additive (revue 2026-08-11, enrichissement utilisateur)
        const v = randomChoice([40, 50, 60, 70]);
        const t1 = randomChoice([2, 3]);
        const t2 = randomChoice([4, 5]);
        const d1 = v * t1;
        const d2 = v * t2;
        reponse = d1 + d2;
        texte = `<p>La distance est proportionnelle à la durée. À vitesse constante, une voiture parcourt ${d1} km en ${t1} h et ${d2} km en ${t2} h. Quelle distance parcourt-elle en ${t1 + t2} h ?</p>`;
        suffixe = ' km';
    }

    const payload = makeSaisie(fmt(reponse), texte, {
        ...opts(L_LIN_A),
        strict: false,
        suffixe,
    });
    payload._meta.generatorKey = 'linearite_add_saisie';
    return payload;
};

// ─── Format 4 — Coefficient (saisie) ─────────────────────────

// Anti-répétition immédiate (revue 2026-08-11, recette standard).
let derniereSignatureCoef = null;

const genCoefficientSaisie684 = () => {
    let payload = construireCoefficientSaisie684();
    for (let tries = 0; tries < 12 && payload.texte === derniereSignatureCoef; tries++) {
        payload = construireCoefficientSaisie684();
    }
    derniereSignatureCoef = payload.texte;
    return payload;
};

const construireCoefficientSaisie684 = () => {
    const r = Math.random();
    let texte, reponse, suffixe;

    if (r < 0.25) {
        // V1 — coefficient explicite distance/durée
        const k = randomChoice([40, 50, 60, 70, 80, 90]);
        const t = randomChoice([2, 3, 4, 5]);
        reponse = k * t;
        texte = `<p>Un véhicule roule à la vitesse constante de ${k} km/h. Quelle distance parcourt-il en ${t} heures ?</p>`;
        suffixe = ' km';
    } else if (r < 0.5) {
        // V2 — couple donné, valeur à calculer via coefficient implicite
        const unit = randomChoice([1.5, 2, 2.5, 3, 4]);
        const a = randomChoice([2, 3, 4, 5]);
        const b = randomChoice([6, 7, 8, 9, 10]);
        const p = a * unit;
        reponse = unit * b;
        texte = `<p>Pour ${a} objets identiques, on paie ${fmt(p)} €. Combien paie-t-on pour ${b} objets ?</p>`;
        suffixe = ' €';
    } else if (r < 0.75) {
        // V3 — peinture m²/L
        const s = randomChoice([8, 10, 12, 15]);
        const L = randomChoice([2, 3, 4, 5, 6]);
        reponse = s * L;
        texte = `<p>Avec 1 L de peinture, on couvre ${s} m². Combien de m² couvrent ${L} L ?</p>`;
        suffixe = ' m²';
    } else {
        // V4 — consommation carburant
        const c = randomChoice([4, 5, 6, 7, 8]);
        const k = randomChoice([2, 3, 4, 5]);
        reponse = c * k;
        // Sujet introduit (revue 2026-08-11) : « La consommation est de… »
        // démarrait sans dire de quoi.
        texte = `<p>La consommation d'une voiture est de ${c} L aux 100 km. Quelle est sa consommation pour ${k * 100} km au même régime ?</p>`;
        suffixe = ' L';
    }

    const payload = makeSaisie(fmt(reponse), texte, {
        ...opts(L_COEF),
        strict: false,
        suffixe,
    });
    // Calculatrice AUTORISÉE (décision utilisateur 2026-08-11) : la variante
    // objets passe par une division décimale (10 ÷ 4 puis × 7).
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'coefficient_saisie';
    return payload;
};

// ─── Format 5 — Procédure (QCM) ──────────────────────────────

const genProcedureQcm = () => {
    const r = Math.random();
    let question, bonne, distracteurs;

    // Revue 2026-08-11 : critère explicite « en une seule étape » sur V1 et V4
    // (le retour à l'unité, procédure VALIDE, y est servi en distracteur — il
    // est écarté loyalement par le critère, plus par un flou) ; « (inutile
    // ici) » retiré (le distracteur s'auto-dénonçait) ; longueurs encadrantes.
    if (r < 0.25) {
        // V1 — multiple évident, linéarité multiplicative
        const a = randomChoice([2, 3]);
        const k = randomChoice([3, 4, 5]);
        const unit = randomChoice([2, 3, 4]);
        const fruit = randomChoice(FRUITS);
        const p = a * unit;
        const bigA = k * a;
        question = `${a} kg de ${fruit} coûtent ${fmt(p)} €. On veut connaître le prix de ${bigA} kg de ${fruit}. Quelle est la procédure la plus rapide, en une seule étape ?`;
        bonne = `Multiplier ${fmt(p)} € par ${k}, car ${bigA} est ${k} fois plus grand que ${a}.`;
        distracteurs = [
            `Calculer d'abord le prix de 1 kg en divisant ${fmt(p)} par ${a}, puis multiplier par ${bigA}.`,
            `Additionner ${fmt(p)} € et ${a} kg pour trouver la réponse.`,
            `Soustraire ${a} à ${bigA} pour connaître le prix à ajouter.`,
        ];
    } else if (r < 0.5) {
        // V2 — valeurs « non alignées », retour à l'unité
        const a = randomChoice([5, 4]);
        const unit = randomChoice([1.5, 2, 2.5]);
        const p = a * unit;
        const b = randomChoice([7, 9, 11]);
        const fruit = randomChoice(FRUITS);
        question = `${a} kg de ${fruit} coûtent ${fmt(p)} €. On veut connaître le prix de ${b} kg. Quelle est la procédure la plus adaptée ?`;
        bonne = `Calculer d'abord le prix de 1 kg en divisant ${fmt(p)} € par ${a}, puis multiplier par ${b}.`;
        distracteurs = [
            `Multiplier directement le prix ${fmt(p)} € par la masse ${b} kg, sans chercher le prix d'un seul kilogramme.`,
            `Additionner le prix ${fmt(p)} € et la masse ${b} kg, puis écrire le résultat obtenu comme prix final.`,
            `Soustraire ${a} de ${b}, puis multiplier le résultat obtenu par le prix ${fmt(p)} € pour conclure.`,
        ];
    } else if (r < 0.75) {
        // V3 — additivité naturelle
        const unit = randomChoice([1.5, 2, 3]);
        const a = randomChoice([2, 3]);
        const b = randomChoice([4, 5]);
        const p1 = a * unit;
        const p2 = b * unit;
        const fruit = randomChoice(FRUITS);
        question = `${a} kg de ${fruit} coûtent ${fmt(p1)} € et ${b} kg de ${fruit} coûtent ${fmt(p2)} €. On veut connaître le prix de ${a + b} kg. Quelle est la procédure la plus rapide, en une seule étape ?`;
        bonne = `Additionner les deux prix : ${fmt(p1)} € + ${fmt(p2)} €.`;
        distracteurs = [
            `Multiplier le premier prix ${fmt(p1)} € par la masse totale ${a + b} kg.`,
            `Diviser le deuxième prix ${fmt(p2)} € par la masse ${b} kg.`,
            `Soustraire ${fmt(p1)} € à ${fmt(p2)} €.`,
        ];
    } else {
        // V4 — coefficient direct
        const v = randomChoice([50, 60, 70, 80]);
        const t = randomChoice([2, 3, 4]);
        question = `Une voiture roule à la vitesse constante de ${v} km/h. On veut connaître la distance parcourue en ${t} h. Quelle est la procédure la plus rapide, en une seule étape ?`;
        bonne = `Multiplier la vitesse par la durée : ${v} × ${t}.`;
        distracteurs = [
            `Diviser la vitesse par la durée : ${v} ÷ ${t}.`,
            `Additionner la vitesse et la durée : ${v} + ${t}.`,
            `Calculer d'abord la distance pour 1 h, puis multiplier par ${t}.`,
        ];
    }

    const choix = [bonne, ...distracteurs];
    const texte = `<p>${question}</p>`;
    const payload = makeQcm(choix, bonne, texte, opts(L_PROC));
    payload._meta.generatorKey = 'procedure_qcm';
    return payload;
};

// ─── Format 6 — Recette (saisie) ─────────────────────────────

const RECIPES = [
    { plat: 'gâteau au chocolat', ing: 'farine', unit: 'g',     qPerPerson: 30 },
    { plat: 'gâteau au chocolat', ing: 'sucre', unit: 'g',      qPerPerson: 25 },
    { plat: 'pâte à crêpes', ing: 'farine', unit: 'g',          qPerPerson: 50 },
    { plat: 'pâte à crêpes', ing: 'lait', unit: 'cL',           qPerPerson: 15 },
    { plat: 'cake', ing: 'beurre', unit: 'g',                   qPerPerson: 20 },
    { plat: 'cake', ing: 'sucre', unit: 'g',                    qPerPerson: 30 },
    { plat: 'tarte aux pommes', ing: 'farine', unit: 'g',       qPerPerson: 40 },
    { plat: 'tarte aux pommes', ing: 'beurre', unit: 'g',       qPerPerson: 15 },
    { plat: 'soupe', ing: 'lait', unit: 'cL',                   qPerPerson: 10 },
    { plat: 'soupe', ing: 'farine', unit: 'g',                  qPerPerson: 8 },
    { plat: 'gâteau au yaourt', ing: 'sucre', unit: 'g',        qPerPerson: 20 },
    { plat: 'gâteau au yaourt', ing: 'farine', unit: 'g',       qPerPerson: 45 },
];

// Anti-répétition immédiate (revue 2026-08-11, recette standard).
let derniereSignatureRecette = null;

const genRecetteSaisie = () => {
    let payload = construireRecetteSaisie();
    for (let tries = 0; tries < 12 && payload.texte === derniereSignatureRecette; tries++) {
        payload = construireRecetteSaisie();
    }
    derniereSignatureRecette = payload.texte;
    return payload;
};

const construireRecetteSaisie = () => {
    const recipe = randomChoice(RECIPES);
    const n = randomChoice([4, 6, 8]);
    // m est un multiple ou un diviseur simple de n
    const factor = randomChoice([2, 3, 4]);
    const useDivision = Math.random() < 0.3 && n % 2 === 0;
    const m = useDivision ? n / 2 : factor * n;

    const q = recipe.qPerPerson * n;
    const reponse = recipe.qPerPerson * m;

    // « en respectant les proportions » (revue 2026-08-11) : « au même
    // rythme » est un vocabulaire de vitesse, une recette n'en a pas.
    const texte = `<p>Une recette de ${recipe.plat} pour ${n} personnes utilise ${q} ${recipe.unit} de ${recipe.ing}. Quelle quantité de ${recipe.ing} faut-il prévoir pour ${m} personnes, en respectant les proportions ?</p>`;

    const payload = makeSaisie(fmt(reponse), texte, {
        ...opts(L_REC),
        strict: false,
        suffixe: ` ${recipe.unit}`,
    });
    // Calculatrice AUTORISÉE (décision utilisateur 2026-08-11) : produits
    // jusqu'à 45 × 24, cohérence avec les autres saisies du générateur.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'recette_saisie';
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    retour_unite_saisie:     genRetourUniteSaisie,
    linearite_mult_saisie:   genLineariteMultSaisie,
    linearite_add_saisie:    genLineariteAddSaisie,
    coefficient_saisie:      genCoefficientSaisie684,
    procedure_qcm:           genProcedureQcm,
    recette_saisie:          genRecetteSaisie,
};

export const CATEGORIES = [
    {
        id: 'proportionnalite_procedures',
        label: 'Résoudre des problèmes de proportionnalité (procédures)',
        formatsList: [
            { key: 'retour_unite_saisie',   label: L_RET },
            { key: 'linearite_mult_saisie', label: L_LIN_M },
            { key: 'linearite_add_saisie',  label: L_LIN_A },
            { key: 'coefficient_saisie',    label: L_COEF },
            { key: 'procedure_qcm',         label: L_PROC },
            { key: 'recette_saisie',        label: L_REC },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    FRUITS,
    RETOUR_CONFIGS,
    RECIPES,
    fmt,
    genRetourUniteSaisie,
    genLineariteMultSaisie,
    genLineariteAddSaisie,
    genCoefficientSaisie684,
    genProcedureQcm,
    genRecetteSaisie,
};
